import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import api, { type ApiResponse } from "../../../api";
import { useToast } from "../../../mantine/notifications/useToast";
import type { Person, Todo, User } from "../../../utils/backend-types";
import { TodoStatus } from "../../../utils/backend-types";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { CustomFormFieldWrapper } from "../../BuildingBlocks/Forms/MantineFormFields/CustomFormFieldWrapper/CustomFormFieldWrapper";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import { Mode } from "../Todos.types";
import { getTodoStatusOptions } from "../utils/getTodoStatusOptions";
import { DueDateWithOverdueFields } from "./DueDateWithOverdueFields/DueDateWithOverdueFields";
import { RegulatoryDutySelect } from "./RegulatoryDutySelect/RegulatoryDutySelect";
import {
  DEFAULT_TODO_FORM_VALUES,
  TODO_FORM_FIELD_DATA,
  type TodoFormValues
} from "./TodoForm.constants";

interface TodoFormProps {
  disableLabelEdit?: boolean;
  initialValues?: Todo;
  mode?: Mode;
  patchUrl?: string;
  persons: Array<Person>;
  postUrl?: string;
  users: Array<User>;
  variantId: number;
  onSubmit: (data: Todo) => Promise<void> | void;
  onSubmittingChange: (isSubmitting: boolean) => void;
}

function TodoForm({
  disableLabelEdit,
  initialValues,
  mode,
  patchUrl,
  persons,
  postUrl,
  users,
  variantId,
  onSubmit,
  onSubmittingChange
}: TodoFormProps) {
  const { shouldShowStaffView } = useShouldShowStaffView();
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const universal = mode === Mode.Universal;

  const personOptions = persons.map((p) => ({ value: p.id, label: p.name }));
  const userOptions = users.map((u) => ({ value: u.id, label: u.name }));
  const statusOptions = getTodoStatusOptions(
    initialValues?.status ?? TodoStatus.Open,
    initialValues?.label ?? "",
    shouldShowStaffView
  );

  const defaultValues: TodoFormValues = initialValues
    ? {
        label: initialValues.label,
        regulatoryDutyId: initialValues.regulatoryDuty?.id ?? null,
        personId:
          persons.find((p) => p.name === initialValues.person)?.id ?? null,
        dueDate: initialValues.dueDate,
        overdue: initialValues.overdue,
        responsible: initialValues.responsible,
        priority: initialValues.priority,
        status: initialValues.status
      }
    : DEFAULT_TODO_FORM_VALUES;

  const { control, handleSubmit, setError, watch } = useForm<TodoFormValues>({
    defaultValues
  });

  const onValidSubmit: SubmitHandler<TodoFormValues> = async (data) => {
    onSubmittingChange(true);

    try {
      const payload = {
        label: data.label,
        regulatoryDuty: data.regulatoryDutyId,
        person: persons.find((p) => p.id === data.personId)?.id ?? null,
        dueDate: data.overdue ? null : data.dueDate,
        overdue: data.overdue,
        responsible: data.responsible,
        priority: data.priority,
        status: data.status,
        variant: variantId
      };

      let response: ApiResponse<Todo>;

      if (patchUrl) {
        response = await api.patch<Todo>(patchUrl, payload);
      } else if (postUrl) {
        response = await api.post<Todo>(postUrl, payload);
      } else {
        console.error("TodoForm: No postUrl or patchUrl provided");
        throw new Error();
      }

      await onSubmit(response.data);
    } catch (error) {
      setErrorsFromResponseData<TodoFormValues>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    } finally {
      onSubmittingChange(false);
    }
  };

  return (
    <form id="todo-form" onSubmit={handleSubmit(onValidSubmit)}>
      <FormFieldController
        control={control}
        data={{ ...TODO_FORM_FIELD_DATA.label, disabled: !!disableLabelEdit }}
      />

      <Controller
        control={control}
        name={TODO_FORM_FIELD_DATA.regulatoryDutyId.name}
        render={({ field }) => (
          <CustomFormFieldWrapper {...TODO_FORM_FIELD_DATA.regulatoryDutyId}>
            <RegulatoryDutySelect
              id="tf-regulatory-duty"
              value={field.value}
              onChange={field.onChange}
            />
          </CustomFormFieldWrapper>
        )}
      />

      <FormFieldController
        control={control}
        data={{ ...TODO_FORM_FIELD_DATA.personId, data: personOptions }}
      />

      <DueDateWithOverdueFields control={control} />

      <FormFieldController
        control={control}
        data={{
          ...TODO_FORM_FIELD_DATA.responsible,
          disabled: universal,
          data: userOptions
        }}
      />

      <FormFieldController
        control={control}
        data={TODO_FORM_FIELD_DATA.priority}
      />

      <FormFieldController
        control={control}
        data={{ ...TODO_FORM_FIELD_DATA.status, data: statusOptions }}
      />
    </form>
  );
}

export { TodoForm };
